import { useEffect, useState } from "react";
import { Download, X, Share, Plus, Smartphone, MonitorDown } from "lucide-react";
import { getDeferredPrompt, clearDeferredPrompt, onInstallChange } from "@/lib/pwaInstall";

export function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

// Always-visible "Install app" control for the top nav. Triggers the native
// install when the browser offers one; otherwise opens per-device steps.
// Hidden once the app is already installed (running standalone).
export const InstallButton = ({ mobile = false, onNavigate }) => {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    if (isStandalone()) {
      setInstalled(true);
      return;
    }
    setDeferred(getDeferredPrompt());
    const unsub = onInstallChange((p) => setDeferred(p));
    const onInstalledEvt = () => setInstalled(true);
    window.addEventListener("appinstalled", onInstalledEvt);
    return () => {
      unsub();
      window.removeEventListener("appinstalled", onInstalledEvt);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      clearDeferredPrompt();
      setDeferred(null);
      if (outcome === "accepted") setInstalled(true);
      return;
    }
    // No native prompt available (desktop Chrome, iOS, Firefox…) → show steps.
    setShowHelp(true);
    onNavigate?.();
  };

  const btnClass = mobile
    ? "mt-1 flex items-center justify-center gap-2 font-display font-800 border border-zinc-300 bg-white px-5 py-3 rounded-full"
    : "ml-1 flex items-center gap-1.5 font-display font-800 text-[13px] whitespace-nowrap border border-zinc-300 bg-white text-black px-3.5 py-2 rounded-full hover:bg-black hover:text-white transition-colors";

  return (
    <>
      <button data-testid={mobile ? "nav-install-btn-mobile" : "nav-install-btn"} onClick={handleClick} className={btnClass}>
        <Download size={mobile ? 18 : 15} />
        Install app
      </button>

      {showHelp && <InstallHelp onClose={() => setShowHelp(false)} />}
    </>
  );
};

function Step({ n, children }) {
  return (
    <div className="flex items-start gap-3">
      <span className="shrink-0 w-6 h-6 rounded-full bg-[#DF3226] text-white flex items-center justify-center text-xs font-800 mt-0.5">
        {n}
      </span>
      <p className="font-body text-sm text-black/75 leading-relaxed">{children}</p>
    </div>
  );
}

export function InstallHelp({ onClose }) {
  const ios = isIOS();
  return (
    <div
      data-testid="install-help-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center p-5 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md bg-white rounded-3xl shadow-brut border border-zinc-200 p-6 sm:p-7 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-[#DF3226] text-white flex items-center justify-center">
              <Download size={20} />
            </div>
            <h2 className="font-display font-900 tracking-tight text-xl">Install the app</h2>
          </div>
          <button
            data-testid="install-help-close"
            onClick={onClose}
            aria-label="Close"
            className="w-9 h-9 rounded-full border border-zinc-200 flex items-center justify-center hover:bg-zinc-100 transition"
          >
            <X size={16} />
          </button>
        </div>
        <p className="font-body text-sm text-black/60 mt-2">
          Add CPH Highlights to your home screen so your saved plan is one tap away while you&apos;re out.
        </p>

        {ios ? (
          <div className="mt-5 space-y-3" data-testid="install-help-ios">
            <div className="font-body font-800 text-xs uppercase tracking-wide text-black/40 flex items-center gap-2">
              <Smartphone size={14} /> iPhone / iPad (Safari)
            </div>
            <Step n={1}>
              Tap the <Share size={14} className="inline mx-0.5 -mt-0.5" /> Share button in Safari&apos;s toolbar.
            </Step>
            <Step n={2}>
              Choose <span className="font-700">Add to Home Screen</span>{" "}
              <Plus size={14} className="inline mx-0.5 -mt-0.5" />.
            </Step>
            <Step n={3}>
              Tap <span className="font-700">Add</span> — the app icon appears on your home screen.
            </Step>
          </div>
        ) : (
          <div className="mt-5 space-y-5">
            <div className="space-y-3" data-testid="install-help-android">
              <div className="font-body font-800 text-xs uppercase tracking-wide text-black/40 flex items-center gap-2">
                <Smartphone size={14} /> Android (Chrome)
              </div>
              <Step n={1}>Open the browser menu (⋮, top-right).</Step>
              <Step n={2}>
                Tap <span className="font-700">Install app</span> or{" "}
                <span className="font-700">Add to Home screen</span>.
              </Step>
            </div>
            <div className="space-y-3 border-t border-zinc-100 pt-5" data-testid="install-help-desktop">
              <div className="font-body font-800 text-xs uppercase tracking-wide text-black/40 flex items-center gap-2">
                <MonitorDown size={14} /> Desktop (Chrome / Edge)
              </div>
              <Step n={1}>
                Click the install icon in the address bar (a small screen with a{" "}
                <span className="font-700">↓</span> / <span className="font-700">⊕</span>), near the
                bookmark star.
              </Step>
              <Step n={2}>
                Click <span className="font-700">Install</span>. If you don&apos;t see the icon, browse a
                page or two first and it&apos;ll appear.
              </Step>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
